import { useEffect } from 'react'
import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { getBaseApi } from '../../Utils/common'
import { fetcher } from '../../Utils/fetcher'
import { isFrsRoute } from '../../Utils/frs-helpers'
import { getUnlocodeFromRouteCode } from '../../Utils/unlocode-mapper'
import { format } from 'date-fns'
import { TravelDatesResponse } from './traveldates-types'

export function useLoadTravelDatesIntoContext(currency: string, offerCode: string) {
  const { state, dispatch } = useTravelSearchContext()

  const baseApi = getBaseApi()
  const routeCode = state.selectedRoute?.routeCode
  const salesOwner = state.selectedRoute?.salesOwner
  const unloCode = getUnlocodeFromRouteCode(routeCode ?? '')
  const offerOrProductCode = state.routesApiData?.offerOrProductCode || offerCode || ''

  const buildParams = ({ direction, outboundDate }: { direction: 'outbound' | 'homebound'; outboundDate?: string }) => {
    const params = new URLSearchParams()
    params.append('direction', direction)
    params.append('salesOwner', salesOwner?.toString() ?? '')
    params.append('numberOfAdults', state.selectedPassengers?.adults?.toString() ?? '1')
    params.append('vehicleType', state.selectedVehicle?.code ?? '')
    state.selectedVehicle?.trailer?.code && params.append('trailerType', state.selectedVehicle.trailer.code)
    state.selectedPassengers?.pets && params.append('numberOfPets', state.selectedPassengers.pets.toString())
    state.selectedPassengers?.childrenAges?.forEach((age) => params.append('chdAges', age.toString()))
    params.append('route', unloCode ?? '')
    state.routesApiData?.offerOrProductCode && params.append('offerOrProductCode', state.routesApiData?.offerOrProductCode)

    if (!state.routesApiData?.offerOrProductCode && offerCode !== '') params.append('offerOrProductCode', offerCode)

    if (outboundDate) {
      params.append('outboundDate', outboundDate)
    }

    return params
  }

  // Always fetch outbound on changes to route / vehicle / passengers / offercode
  useEffect(() => {
    if (
      !routeCode ||
      !salesOwner ||
      (!isFrsRoute(routeCode) && (!unloCode || !state.selectedVehicle?.code || state.vehicleApiLoading || !state.selectedPassengers))
    )
      return

    const fetchData = async () => {
      dispatch({ type: 'SET_TRAVEL_DATES_API_LOADING', payload: true })

      try {
        const outboundUrl = isFrsRoute(routeCode)
          ? `${baseApi}/available-travel-dates-frs?routeCode=${routeCode}&currencyCode=${currency}`
          : `${baseApi}/available-travel-dates?${buildParams({ direction: 'outbound' }).toString()}`

        const [outboundData] = await Promise.all([fetcher<TravelDatesResponse>(outboundUrl)])

        dispatch({ type: 'SET_OUTBOUND_TRAVEL_DATES', payload: outboundData })
      } catch (error) {
        console.error('Failed to fetch travel dates', error)
      } finally {
        dispatch({ type: 'SET_TRAVEL_DATES_API_LOADING', payload: false })
      }
    }

    fetchData()
  }, [
    routeCode,
    salesOwner,
    unloCode,
    state.selectedPassengers,
    state.selectedVehicle?.code,
    offerOrProductCode,
    currency,
    state.vehicleApiLoading,
  ])

  // Refetch ONLY homebound when outbound date changes
  useEffect(() => {
    if (!routeCode || !salesOwner || (!isFrsRoute(routeCode) && !unloCode) || !state.selectedDates.outboundDate) return

    const fetchHomebound = async () => {
      dispatch({ type: 'SET_TRAVEL_DATES_API_LOADING', payload: true })

      try {
        const homeboundUrl = isFrsRoute(routeCode)
          ? `${baseApi}/available-travel-dates-frs?routeCode=${routeCode}&currencyCode=${currency}`
          : `${baseApi}/available-travel-dates?${buildParams({
              direction: 'homebound',
              outboundDate: state.selectedDates?.outboundDate
                ? format(state.selectedDates.outboundDate, 'yyyy-MM-dd')
                : format(new Date(), 'yyyy-MM-dd'),
            }).toString()}`

        const homeboundData = await fetcher<TravelDatesResponse>(homeboundUrl)
        dispatch({ type: 'SET_HOMEBOUND_TRAVEL_DATES', payload: homeboundData })
      } catch (error) {
        console.error('Failed to fetch homebound travel dates', error)
      } finally {
        dispatch({ type: 'SET_TRAVEL_DATES_API_LOADING', payload: false })
      }
    }

    fetchHomebound()
  }, [state.selectedDates.outboundDate, routeCode, salesOwner, unloCode, offerOrProductCode, currency])
}
